import { clsx } from 'clsx';
import * as React from 'react';

import type * as api from '@gitbook/api-client';

import type { ContentKitElementProps } from './types';

export const ContentKitStackDirectionContent = React.createContext<'hstack' | 'vstack'>('vstack');

/**
 * Component to render a vstack or hstack element.
 */
export function ContentKitStack(
    props: ContentKitElementProps<api.ContentKitHStack | api.ContentKitVStack>
): React.ReactElement {
    const { element, renderElement } = props;

    return (
        <ContentKitStackDirectionContent.Provider value={element.type}>
            <div
                className={clsx(
                    'flex',
                    element.type === 'hstack' ? 'flex-row items-center' : 'flex-col',
                    element.type === 'vstack' && element.align === 'end' && 'items-end',
                    element.type === 'vstack' && element.align === 'center' && 'items-center',
                    element.type === 'hstack' && element.align === 'end' && 'justify-end',
                    element.type === 'hstack' && element.align === 'center' && 'justify-center'
                )}
            >
                {element.children.map((child, index) => {
                    const next = element.children[index + 1];
                    const spacingKey = next ? getStackElementsSpacing(child, next) : null;

                    return (
                        <div
                            key={index}
                            className={clsx(
                                element.type === 'hstack' && spacingKey === '200' && 'pr-2',
                                element.type === 'hstack' && spacingKey === '400' && 'pr-4',
                                element.type === 'hstack' && spacingKey === '600' && 'pr-6',
                                element.type === 'vstack' && spacingKey === '200' && 'pb-2',
                                element.type === 'vstack' && spacingKey === '400' && 'pb-4',
                                element.type === 'vstack' && spacingKey === '600' && 'pb-6'
                            )}
                            style={child.type === 'box' ? { flexGrow: child.grow || 0 } : undefined}
                        >
                            {renderElement(child)}
                        </div>
                    );
                })}
            </div>
        </ContentKitStackDirectionContent.Provider>
    );
}

/**
 * Ensure a set of elements are going to be rendered as a stack (either hstack or vstack).
 * If the elements are already a stack, return them as is.
 */
export function ensureStackElements(
    type: 'hstack' | 'vstack',
    elements: api.ContentKitDescendantElement[],
    align?: 'start' | 'center' | 'end'
): api.ContentKitDescendantElement[] {
    if (elements.length === 1) {
        return elements;
    }

    return [{ type, align, children: elements }];
}

/**
 * Get the spacing between two elements in a stack.
 */
function getStackElementsSpacing(
    a: api.ContentKitDescendantElement,
    b: api.ContentKitDescendantElement
): '200' | '400' | '600' | null {
    if (a.type === 'divider' || b.type === 'divider') {
        return null;
    }

    if (a.type === 'input' || b.type === 'input' || a.type === 'box' || b.type === 'box') {
        return '400';
    }

    return '200';
}
